// src/components/festival/SponsorshipSupport.tsx
// DROP 40D — ERUE Festival Suite Sponsorship & Support
// DROP 48 — Cleanup: removed mock multipliers, prompt(), admin endpoint
// Real Paystack, dark theme, live fundraising stats, proper pledge modal

import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Heart, Crown, CheckCircle, AlertTriangle, ExternalLink } from "lucide-react";
import PrintButton from "../shared/PrintButton";
import { printHtml } from "../../utils/printUtils";

const C = {
  bg: "#070D0A", surface: "#0E1812", card: "#121E16", border: "#1C2E22",
  text: "#EDE8DC", muted: "#9CA38F",
  gold: "#C9A84C", goldLt: "#E2C97E",
  green: "#0F5132", greenLt: "#4ADE80",
  red: "#ef4444", orange: "#f97316", blue: "#60a5fa",
};

const fmt = (n: number) => "₦" + n.toLocaleString("en-NG");

const SPONSOR_TIERS = [
  {
    id: "royal-patron", tier: "Royal Patron", emoji: "\uD83D\uDC51",
    amount: 10_000_000, color: "#C9A84C", badge: "PLATINUM",
    benefits: [
      "Exclusive naming right on Grand Finale stage",
      "VIP Royal Tent with HRH Ogieneni — all 7 days",
      "Full-page spread in official festival programme",
      "10 VIP complimentary passes + dedicated parking",
      "Live mention at all 7 festival sessions",
      "Corporate logo on all digital and print media",
      "Official Certificate of Royal Commendation",
      "Seat on Festival Advisory Council for 2026",
    ],
  },
  {
    id: "gold-sponsor", tier: "Gold Sponsor", emoji: "\uD83E\uDD47",
    amount: 5_000_000, color: "#E2C97E", badge: "GOLD",
    benefits: [
      "Branded event pavilion at Palace grounds",
      "6 VIP passes + reserved seating at Grand Finale",
      "Half-page in official festival programme",
      "Logo on festival website, banners and merchandise",
      "Mention in opening ceremony address",
      "Social media recognition across all channels",
      "Certificate of Appreciation from Ogieneni",
    ],
  },
  {
    id: "silver-sponsor", tier: "Silver Sponsor", emoji: "\uD83E\uDD48",
    amount: 2_000_000, color: "#9CA3AF", badge: "SILVER",
    benefits: [
      "Branded stall at Festival Market Village",
      "4 General admission passes",
      "Quarter-page in official festival programme",
      "Logo on festival website",
      "Social media shoutout (3 platforms)",
      "Certificate of Recognition",
    ],
  },
  {
    id: "community-patron", tier: "Community Patron", emoji: "\uD83E\uDD1D",
    amount: 500_000, color: "#4ADE80", badge: "COMMUNITY",
    benefits: [
      "Acknowledgement in festival programme",
      "2 General admission passes",
      "Social media shoutout",
      "Certificate of Community Patronage",
    ],
  },
];

const QUICK_AMOUNTS = [10_000, 25_000, 50_000, 100_000, 250_000, 500_000];
const GOAL_TOTAL = 83_000_000;
const SUB_GOALS = [
  { label: "Programme & Entertainment", target: 12_000_000, color: C.gold },
  { label: "Venue & Infrastructure",    target: 14_000_000, color: C.blue },
  { label: "Hospitality & Catering",    target: 10_000_000, color: C.greenLt },
  { label: "Security & Medical",        target: 5_500_000,  color: C.orange },
];

function printDonationReceipt(name: string, email: string, amount: number, ref: string, message?: string) {
  printHtml(`
    <style>
      body{font-family:serif;padding:40px;color:#222}
      h1{color:#0F5132;font-size:24px}
      .amount{font-size:28px;font-weight:900;color:#0F5132}
      .ref{font-size:11px;color:#888;letter-spacing:1px}
      .divider{border-top:2px solid #0F5132;margin:20px 0}
    </style>
    <h1>ERUE Festival 2026 — Donation Receipt</h1>
    <div class="divider"></div>
    <p><strong>Donor:</strong> ${name}</p>
    <p><strong>Email:</strong> ${email}</p>
    <div style="font-size:10px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">Amount Donated</div>
    <div class="amount">${fmt(amount)}</div>
    <div class="divider"></div>
    <p class="ref">Reference: ${ref}</p>
    ${message ? `<p><em>"${message}"</em></p>` : ''}
    <p style="font-size:11px;color:#888">Thank you for supporting the ERUE Festival of Uzairue Kingdom, December 24-30, 2026.</p>
  `);
}

function ProgressBar({ raised, goal, color = C.gold }: { raised: number; goal: number; color?: string }) {
  const pct = Math.min(100, goal > 0 ? (raised / goal) * 100 : 0);
  return (
    <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
      <div style={{
        height: "100%", width: `${pct}%`, background: color,
        borderRadius: 4, transition: "width 0.8s ease",
      }} />
    </div>
  );
}

export const SponsorshipSupport: React.FC = () => {
  const [totalRaised,    setTotalRaised]    = useState(0);
  const [donorCount,     setDonorCount]     = useState(0);
  const [statsLoading,   setStatsLoading]   = useState(true);
  const [donorName,      setDonorName]      = useState("");
  const [donorEmail,     setDonorEmail]     = useState("");
  const [donorPhone,     setDonorPhone]     = useState("");
  const [donorMessage,   setDonorMessage]   = useState("");
  const [amount,         setAmount]         = useState(50_000);
  const [customAmount,   setCustomAmount]   = useState("");
  const [useCustom,      setUseCustom]      = useState(false);
  const [payLoading,     setPayLoading]     = useState(false);
  const [payError,       setPayError]       = useState<string | null>(null);
  const [lastRef,        setLastRef]        = useState<string | null>(null);
  const [success,        setSuccess]        = useState(false);
  const [paidAmount,     setPaidAmount]     = useState(0);
  const [pledging,       setPledging]       = useState<string | null>(null);
  const [pledgeModal,    setPledgeModal]    = useState<typeof SPONSOR_TIERS[0] | null>(null);
  const [pledgeName,     setPledgeName]     = useState("");
  const [pledgeEmail,    setPledgeEmail]    = useState("");
  const [pledgePhone,    setPledgePhone]    = useState("");

  const finalAmount = useCustom ? (parseInt(customAmount.replace(/\D/g, "")) || 0) : amount;

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res  = await fetch("/api/fundraising/stats");
      const data = await res.json();
      if (data.success) {
        setTotalRaised(data.totalRaised || 0);
        setDonorCount(data.donorCount || 0);
      }
    } catch { /* silent */ }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDonate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!donorName.trim() || !donorEmail.trim() || finalAmount < 1000) {
      setPayError("Please fill all fields. Minimum donation: ₦1,000.");
      return;
    }
    setPayLoading(true); setPayError(null);
    try {
      const res  = await fetch("/api/fundraising/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:    donorName.trim(),
          email:   donorEmail.trim(),
          phone:   donorPhone.trim(),
          message: donorMessage.trim(),
          amount:  finalAmount,
          campaignId: "erue-festival-2026-main",
        }),
      });
      const data = await res.json();
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else if (data.success && data.reference) {
        setLastRef(data.reference);
        setPaidAmount(finalAmount);
        setSuccess(true);
        fetchStats();
      } else {
        setPayError(data.error || "Payment initiation failed. Please try again.");
      }
    } catch {
      setPayError("Network error — please check your connection and retry.");
    } finally { setPayLoading(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref    = params.get("reference") || params.get("trxref");
    if (ref && !success) {
      fetch("/api/fundraising/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: ref }),
      }).then(r => r.json()).then(data => {
        if (data.success) {
          setLastRef(ref);
          setPaidAmount(data.amount || finalAmount);
          setSuccess(true);
          fetchStats();
        }
      }).catch(() => {});
    }
  }, []);

  const handlePledge = async (tier: typeof SPONSOR_TIERS[0]) => {
    const email = pledgeEmail.trim();
    if (!email || !pledgeName.trim()) {
      setPayError("Please fill your name and email to proceed.");
      return;
    }
    setPledging(tier.id); setPayError(null); setPledgeModal(null);
    try {
      const res  = await fetch("/api/fundraising/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: pledgeName.trim(), email: email, phone: pledgePhone.trim(), amount: tier.amount,
          message: `${tier.tier} Sponsorship Package — ERUE Festival 2026`,
          campaignId: "erue-festival-2026-main",
        }),
      });
      const data = await res.json();
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else if (data.success) {
        alert(`\u2705 ${tier.tier} pledge recorded! Reference: ${data.reference}`);
      } else {
        setPayError(data.error || "Pledge failed.");
      }
    } catch { setPayError("Network error — please retry."); }
    finally { setPledging(null); }
  };

  const pct       = GOAL_TOTAL > 0 ? Math.min(100, (totalRaised / GOAL_TOTAL) * 100) : 0;
  const remaining = Math.max(0, GOAL_TOTAL - totalRaised);

  if (success && lastRef) {
    return (
      <div style={{ background: C.bg, minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 40, maxWidth: 480, textAlign: "center" }}>
          <CheckCircle size={48} style={{ color: C.greenLt, margin: "0 auto 16px" }} />
          <h2 style={{ fontFamily: "Cinzel, serif", color: C.gold, fontSize: 22, marginBottom: 8 }}>
            Thank You for Your Support!
          </h2>
          <p style={{ color: C.muted, fontSize: 14, marginBottom: 20 }}>
            Your donation of <strong style={{ color: C.greenLt }}>{fmt(paidAmount)}</strong> has been received.
          </p>
          <div style={{ background: C.surface, borderRadius: 8, padding: "12px 20px", marginBottom: 24 }}>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>Reference</div>
            <div style={{ fontSize: 13, color: C.text, fontFamily: "monospace", marginTop: 4 }}>{lastRef}</div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => printDonationReceipt(donorName, donorEmail, paidAmount, lastRef || "", donorMessage)} style={{ padding:"10px 20px", borderRadius:8, border:`1px solid ${C.gold}`, background:"none", color:C.gold, cursor:"pointer", fontSize:13, fontWeight:600 }}>Print Receipt</button>
            <button onClick={() => { setSuccess(false); setLastRef(null); fetchStats(); }}
              style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.border}`,
                background: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}>
              Make Another Donation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "80vh", paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "20px 24px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.gold, fontFamily: "Cinzel, serif", margin: 0 }}>
              Support & Sponsor ERUE Festival 2026
            </h2>
            <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>
              Uzairue Heritage: Culture, Security, Prosperity — December 24-30, 2026
            </p>
          </div>
          <button onClick={fetchStats} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "7px 12px",
            background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
            color: C.muted, fontSize: 11, cursor: "pointer",
          }}>
            <RefreshCw size={12}/> Refresh
          </button>
        </div>

        {/* Fundraising stats */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Total Raised</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: C.greenLt, fontFamily: "Cinzel, serif" }}>
              {statsLoading ? "..." : fmt(totalRaised)}
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>of {fmt(GOAL_TOTAL)} goal</div>
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Funded</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: C.gold, fontFamily: "Cinzel, serif" }}>
              {statsLoading ? "..." : pct.toFixed(1) + "%"}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Donors</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: C.blue, fontFamily: "Cinzel, serif" }}>
              {statsLoading ? "..." : donorCount.toLocaleString()}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Still Needed</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.orange, fontFamily: "Cinzel, serif" }}>
              {statsLoading ? "..." : fmt(remaining)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <ProgressBar raised={totalRaised} goal={GOAL_TOTAL} color={`linear-gradient(90deg, ${C.greenLt}, ${C.gold})`} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.muted, marginBottom: 20 }}>
          <span>₦0</span>
          <span style={{ color: C.gold }}>₦25M</span>
          <span style={{ color: C.gold }}>₦50M</span>
          <span style={{ color: C.gold }}>₦83M 🎯</span>
        </div>

        {/* Sub-goals */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {SUB_GOALS.map(g => (
            <div key={g.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: C.text, fontWeight: 600, marginBottom: 6 }}>{g.label}</div>
              <ProgressBar raised={totalRaised > 0 ? (totalRaised / GOAL_TOTAL) * g.target : 0} goal={g.target} color={g.color} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9, color: C.muted }}>
                <span>Target: {fmt(g.target)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "24px 24px 0" }}>

        {/* Sponsor Packages */}
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: C.gold, fontFamily: "Cinzel, serif", marginBottom: 6, textAlign: "center" }}>
            🏆 Corporate & Diaspora Sponsorship Packages
          </h2>
          <p style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 20 }}>
            Tailored visibility, VIP access, and royal commendation for corporate partners and diaspora patrons
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
            {SPONSOR_TIERS.map(tier => (
              <div key={tier.id} style={{
                background: C.card, border: `2px solid ${tier.color}44`,
                borderRadius: 12, overflow: "hidden", borderTop: `4px solid ${tier.color}`,
              }}>
                <div style={{ padding: "18px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 32 }}>{tier.emoji}</span>
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, padding: "2px 8px",
                        background: tier.color + "22", color: tier.color,
                        border: `1px solid ${tier.color}44`, borderRadius: 4 }}>{tier.badge}</span>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: tier.color, fontFamily: "Cinzel, serif", marginBottom: 4, marginTop: 4 }}>
                        {tier.tier}
                      </h3>
                      <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{fmt(tier.amount)}</div>
                    </div>
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
                    {tier.benefits.map((b, i) => (
                      <li key={i} style={{ fontSize: 12, color: C.muted, display: "flex", gap: 6, marginBottom: 4 }}>
                        <span style={{ color: tier.color, flexShrink: 0 }}>✓</span>{b}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => { setPledgeModal(tier); setPledgeName(""); setPledgeEmail(""); setPledgePhone(""); setPayError(null); }}
                    disabled={pledging === tier.id}
                    style={{ width: "100%", padding: "10px", borderRadius: 8, border: `1px solid ${tier.color}`,
                      background: tier.color + "15", color: tier.color, fontWeight: 700,
                      fontSize: 13, cursor: "pointer" }}>
                    {pledging === tier.id ? "Submitting..." : "Pledge Now \u2192"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Donation Form */}
        <div style={{ maxWidth: 560, margin: "0 auto 32px" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: C.gold, fontFamily: "Cinzel, serif", marginBottom: 6, textAlign: "center" }}>
            ❤️ Make a Donation
          </h2>
          <p style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 20 }}>
            Every contribution — large or small — helps make the ERUE Festival 2026 a success
          </p>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
            {payError && (
              <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: C.red,
                display: "flex", gap: 8, alignItems: "center" }}>
                <AlertTriangle size={14}/> {payError}
              </div>
            )}

            {/* Quick amounts */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1,
                textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>Select Amount</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {QUICK_AMOUNTS.map(a => (
                  <button key={a} onClick={() => { setAmount(a); setUseCustom(false); }}
                    style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", fontWeight: 600,
                      background: !useCustom && amount === a ? C.green : "transparent",
                      color: !useCustom && amount === a ? C.goldLt : C.muted,
                      border: `1px solid ${!useCustom && amount === a ? C.gold : C.border}` }}>
                    {fmt(a)}
                  </button>
                ))}
                <button onClick={() => setUseCustom(true)}
                  style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", fontWeight: 600,
                    background: useCustom ? C.green : "transparent",
                    color: useCustom ? C.goldLt : C.muted,
                    border: `1px solid ${useCustom ? C.gold : C.border}` }}>
                  Custom
                </button>
              </div>
              {useCustom && (
                <input type="text" placeholder="Enter amount in Naira" value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                    background: "#0a1410", border: `1px solid ${C.border}`, color: C.text,
                    outline: "none", boxSizing: "border-box" }} />
              )}
            </div>

            <form onSubmit={handleDonate}>
              {[
                { label: "Full Name *", val: donorName, set: setDonorName, ph: "Your full name", type: "text" },
                { label: "Email Address *", val: donorEmail, set: setDonorEmail, ph: "your@email.com", type: "email" },
                { label: "Phone Number", val: donorPhone, set: setDonorPhone, ph: "+234 xxx xxx xxxx", type: "tel" },
                { label: "Message (optional)", val: donorMessage, set: setDonorMessage, ph: "Leave a message of support...", type: "text" },
              ].map(f => (
                <div key={f.label} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>{f.label}</label>
                  <input type={f.type} value={f.val} placeholder={f.ph} onChange={e => f.set(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                      background: "#0a1410", border: `1px solid ${C.border}`, color: C.text,
                      outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}

              <button type="submit" disabled={payLoading}
                style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none",
                  background: `linear-gradient(135deg, ${C.green}, #1a6b40)`,
                  color: C.goldLt, fontWeight: 700, fontSize: 15, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8 }}>
                {payLoading
                  ? <><RefreshCw size={15} style={{ animation: "spin 1s linear infinite" }}/> Processing...</>
                  : <><Heart size={15}/> Donate {fmt(finalAmount)}</>}
              </button>
              <p style={{ fontSize: 10, color: C.muted, textAlign: "center", marginTop: 10 }}>
                Secured by Paystack • All transactions encrypted
              </p>
            </form>
          </div>
        </div>

        {/* Contact for large sponsors */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 24 }}>
          <Crown size={24} style={{ color: C.gold, margin: "0 auto 10px" }} />
          <h3 style={{ fontFamily: "Cinzel, serif", color: C.gold, fontSize: 14, marginBottom: 8 }}>
            Bespoke Sponsorship Arrangements
          </h3>
          <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
            For government partnerships, institutional sponsorships, or arrangements above ₦10M,
            contact us directly for a tailored agreement.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://wa.me/2347057840320" target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                background: "#25D366", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
              <ExternalLink size={13}/> WhatsApp
            </a>
            <a href="mailto:erueuzairue@gmail.com"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, fontSize: 12, textDecoration: "none" }}>
              <ExternalLink size={13}/> Email Us
            </a>
          </div>
        </div>
      </div>

      {/* Pledge Modal */}
      {pledgeModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.card, border: `2px solid ${pledgeModal.color}44`,
            borderRadius: 16, width: "100%", maxWidth: 460, padding: 24,
            boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "Cinzel, serif", color: pledgeModal.color, margin: 0, fontSize: 16 }}>
                {pledgeModal.emoji} {pledgeModal.tier} Pledge
              </h3>
              <button onClick={() => setPledgeModal(null)}
                style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 20 }}>
                ✕
              </button>
            </div>
            <div style={{ background: `${pledgeModal.color}11`, border: `1px solid ${pledgeModal.color}33`,
              borderRadius: 8, padding: "8px 14px", marginBottom: 16,
              fontSize: 13, color: pledgeModal.color, fontWeight: 700 }}>
              Sponsorship Amount: {fmt(pledgeModal.amount)}
            </div>
            {payError && (
              <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: C.red }}>
                {payError}
              </div>
            )}
            {[
              { label: "Full Name *", value: pledgeName, set: setPledgeName, ph: "Your full name", type: "text" },
              { label: "Email Address *", value: pledgeEmail, set: setPledgeEmail, ph: "your@email.com", type: "email" },
              { label: "Phone Number", value: pledgePhone, set: setPledgePhone, ph: "+234 xxx xxx xxxx", type: "tel" },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 1,
                  textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>{f.label}</label>
                <input type={f.type} value={f.value} placeholder={f.ph}
                  onChange={e => f.set(e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                    background: "#0a1410", border: `1px solid ${C.border}`, color: C.text,
                    outline: "none", boxSizing: "border-box" }} />
              </div>
            ))}
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
              Our team will contact you within 24 hours to finalise your sponsorship agreement.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setPledgeModal(null)}
                style={{ padding: "8px 16px", borderRadius: 8, background: "transparent",
                  border: `1px solid ${C.border}`, color: C.muted, cursor: "pointer", fontSize: 12 }}>
                Cancel
              </button>
              <button onClick={() => handlePledge(pledgeModal)} disabled={!!pledging}
                style={{ padding: "8px 20px", borderRadius: 8, background: pledgeModal.color,
                  border: "none", color: "#070D0A", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                {pledging === pledgeModal.id ? "Submitting..." : "Submit Pledge \u2192"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
